import { useEffect, useMemo, useState } from "react";
import {
  MRT_BottomToolbar as MRTBottomToolbar,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { Button, Grid2 } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IPlaceSchedule } from "../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { commonLocalization } from "../../../../../../functions/tableLocalization";
import { BookingPropertyRow } from "./typings/BookingPropertyTableRow.type";
import { handleInitialBookingProp } from "./BookingPropertyAssignTable.partial";
import { withSeeditMrtInitialState } from "../../../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../../../themes/use-seedit-mrt-table-options";

type Props = {
  subjectEntity: IPlaceSchedule[] | IZoneSchedule[];
  entries: BookingPropertyRow[] | undefined;
  onAssign: (bookProp: BookingPropertyRow[]) => void;
  onClose: () => void;
};

export function BookingPropertyAssignTable({
  subjectEntity,
  entries,
  onAssign,
  onClose
}: Readonly<Props>) {
  const { t, i18n } = useTranslation();

  const [data, setData] = useState<BookingPropertyRow[]>();
  /** handle row select and handle initial value from floor plan, which value is already assigned */
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  useEffect(() => {
    setData(entries);
  }, [entries]);

  useEffect(() => {
    handleInitialBookingProp(subjectEntity, setRowSelection);
  }, [subjectEntity]);

  const column = useMemo<MRT_ColumnDef<BookingPropertyRow>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("Name"),
        size: 200,
        enableEditing: false
      },
      {
        accessorKey: "description",
        header: t("Description"),
        size: 600,
        enableEditing: false
      }
    ],
    [data, i18n.language]
  );
  const seeditMrtOptions = useSeeditMrtTableOptions<BookingPropertyRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: data ?? [],
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    enableHiding: true,
    enableRowSelection: true,
    getRowId: row => row.id?.toString(),
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 5, pageIndex: 0 }
    }),
    positionToolbarAlertBanner: "none",
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15],
      variant: "outlined"
    },
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("_editable_list_noRecords", { item: t("_noBookingProperty") })
    },
    renderBottomToolbar: ({ table }) => {
      return (
        <Grid2>
          <Grid2>
            <MRTBottomToolbar table={table} />
          </Grid2>
          <Grid2 textAlign={"end"} sx={{ mt: 3, mb: 1 }}>
            <Button data-testid="cancel-btn" onClick={onClose} color="secondary" sx={{ mr: 2 }}>
              {t("Cancel")}
            </Button>
            <Button
              data-testid="assign-btn"
              onClick={() => {
                onAssign(table.getSelectedRowModel().rows.map(row => row.original));
                onClose();
              }}
            >
              {t("Assign")}
            </Button>
          </Grid2>
        </Grid2>
      );
    }
  });

  return (
    <Grid2 container>
      <MaterialReactTable table={table} />
    </Grid2>
  );
}
